// Shared by the floating controls that measure the page on every update and scroll: skip a React
// render when nothing moved, and measure at most once per frame for events that fire faster.

// Any object whose own fields are plain values; interfaces qualify, unlike with a Record type.
type Flat = object;

/** Two flat records hold the same values, compared field by field. */
export function sameFields<T extends Flat>(a: T | null, b: T | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  return keys.every((key) => Object.is(left[key], right[key]));
}

/** The next state, or the current one when it holds the same values, so React bails out. */
export const keepIfSame =
  <T extends Flat>(next: T | null) =>
  (current: T | null): T | null =>
    sameFields(current, next) ? current : next;

/** Lists of flat records, compared element by element. */
export const keepListIfSame =
  <T extends Flat>(next: T[]) =>
  (current: T[]): T[] =>
    current.length === next.length &&
    current.every((entry, index) => sameFields(entry, next[index]!))
      ? current
      : next;

/**
 * Calls `run` once on the next animation frame however often the result is called before then.
 * `cancel` drops a pending call; call it when the listener it serves is removed.
 */
export function perFrame(run: () => void): { schedule: () => void; cancel: () => void } {
  let frame: number | null = null;
  return {
    schedule: () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        run();
      });
    },
    cancel: () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    },
  };
}
